import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleMicrosoftRedirect } from '../services/oauth';
import toast from 'react-hot-toast';
import { ROUTES } from '../constants/routes';

/**
 * Dedicated page for Microsoft login redirect.
 * Microsoft redirects here after sign-in, so we land on a public route
 * and the hash/query is preserved. We process the redirect and complete login.
 */
const MicrosoftLoginCallback = () => {
  const navigate = useNavigate();
  const { oauthLogin } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const run = async () => {
      try {
        const result = await handleMicrosoftRedirect();
        if (result) {
          const loginResult = await oauthLogin(result);
          if (loginResult === 'pending_approval') {
            window.location.href = ROUTES.PENDING_APPROVAL;
            return;
          }
          if (loginResult === 'requires_2fa') {
            window.location.href = ROUTES.TWO_FACTOR_VERIFICATION;
            return;
          }
          if (loginResult === 'suggest_2fa') {
            window.location.href = ROUTES.ONBOARDING;
            return;
          }
          if (loginResult === true) {
            toast.success('Signed in with Microsoft');
            window.location.href = ROUTES.DASHBOARD;
            return;
          }
        }
        setError('Could not complete Microsoft sign-in.');
      } catch (err) {
        console.error('Microsoft login callback error:', err);
        setError(err.response?.data?.detail || err.message || 'Microsoft sign-in failed.');
        toast.error('Microsoft sign-in failed. Please try again.');
      }
    };

    run();
  }, [navigate, oauthLogin]);

  if (error) {
    return (
      <div className="auth-page min-h-screen flex items-center justify-center bg-secondary-50 p-4">
        <div className="bg-white rounded-lg shadow-md border border-secondary-200 p-8 max-w-md w-full text-center">
          <p className="text-red-600 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.LOGIN)}
            className="w-full py-2 px-4 rounded-md bg-primary-600 text-white hover:bg-primary-700"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page min-h-screen flex items-center justify-center bg-secondary-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
        <p className="text-secondary-600">Completing Microsoft sign-in...</p>
      </div>
    </div>
  );
};

export default MicrosoftLoginCallback;
